// pull.ts — transport-agnostic session/collection pull with the same
// verify-then-delete semantics as sessions/pull-latest.sh:
//   1. stream every remote file to the local writer
//   2. re-list the remote AFTER the pull and byte-compare every file
//   3. only if every size matches (and deletion is requested) rm -rf the remote
// No DOM and no browser-only APIs in here — the writer abstraction keeps it
// usable against any FileSystemDirectoryHandle (picked dir or OPFS) and, later,
// a Node fs writer.
import type { Adb } from "@yume-chan/adb";

export const SESSION_ID_RE = /^\d{8}_\d{6}$/;
export const recRoot = (pkg: string) => `/sdcard/Android/data/${pkg}/files/recordings`;
export const colRoot = (pkg: string) => `/sdcard/Android/data/${pkg}/files/collections`;

/** Minimal writable-directory abstraction (browser FS Access / OPFS / node). */
export interface DirWriter {
  /** Create (truncate) a file at the relative path and return its stream. */
  file(rel: string[]): Promise<WritableStream<Uint8Array>>;
  /** Byte size of an existing local file, or null when absent. */
  size(rel: string[]): Promise<number | null>;
  /** Does a top-level entry (session/collection dir) already exist? */
  exists(name: string): Promise<boolean>;
  /** Read a previously-written small text file (collection.json). */
  readText(rel: string[]): Promise<string>;
}

export interface RemoteFile { rel: string[]; size: number; path: string; }
export interface PullProgress {
  phase: "walk" | "pull" | "verify" | "delete" | "done";
  file?: string;
  filesDone: number;
  filesTotal: number;
  bytesDone: number;
  bytesTotal: number;
}
export interface PullResult {
  id: string;
  files: number;
  bytes: number;
  seconds: number;
  verified: boolean;
  mismatches: string[];
  deletedRemote: boolean;
}

const isDir = (type: number) => type === 4;   // LinuxFileType: 4 Dir, 8 File, 10 Link

/** Recursively list all files under a remote dir (sizes from the sync stat). */
export async function walkRemote(adb: Adb, root: string, rel: string[] = []): Promise<RemoteFile[]> {
  const sync = await adb.sync();
  try {
    const out: RemoteFile[] = [];
    const walk = async (dir: string, r: string[]) => {
      const entries = await sync.readdir(dir);
      for (const e of entries) {
        const name = String(e.name);
        if (name === "." || name === "..") continue;
        const p = `${dir}/${name}`;
        if (isDir(Number(e.type))) await walk(p, [...r, name]);
        else out.push({ rel: [...r, name], size: Number(e.size), path: p });
      }
    };
    await walk(root, rel);
    return out;
  } finally {
    await sync.dispose();
  }
}

export interface RemoteSession { id: string; files: number; bytes: number; }

/** Sessions (or collections, given colRoot) on the device, oldest → newest. */
export async function listRemote(adb: Adb, root: string): Promise<RemoteSession[]> {
  const sync = await adb.sync();
  let ids: string[] = [];
  try {
    const entries = await sync.readdir(root);
    ids = entries
      .map((e: any) => String(e.name))
      .filter((n: string) => SESSION_ID_RE.test(n))
      .sort();
  } catch {
    return [];   // root missing (no recordings yet) — not an error
  } finally {
    await sync.dispose();
  }
  const out: RemoteSession[] = [];
  for (const id of ids) {
    const files = await walkRemote(adb, `${root}/${id}`);
    out.push({ id, files: files.length, bytes: files.reduce((a, f) => a + f.size, 0) });
  }
  return out;
}

async function pullFiles(
  adb: Adb, files: RemoteFile[], writer: DirWriter,
  onProgress?: (p: PullProgress) => void,
): Promise<{ bytes: number }> {
  const bytesTotal = files.reduce((a, f) => a + f.size, 0);
  let bytesDone = 0, filesDone = 0;
  // Sessions are dominated by one multi-GB video file, so per-file callbacks
  // freeze the UI for minutes — count streamed bytes and emit ~10×/s.
  let lastEmit = 0;
  const emit = (file: string, fileDone: number, force = false) => {
    const now = Date.now();
    if (!force && now - lastEmit < 100) return;
    lastEmit = now;
    onProgress?.({ phase: "pull", file, filesDone, filesTotal: files.length,
                   bytesDone: bytesDone + fileDone, bytesTotal });
  };
  const sync = await adb.sync();
  try {
    for (const f of files) {
      const rel = f.rel.join("/");
      emit(rel, 0, true);
      const target = await writer.file(f.rel);
      let fileDone = 0;
      const counter = new TransformStream<Uint8Array, Uint8Array>({
        transform(chunk, controller) {
          fileDone += chunk.byteLength;
          emit(rel, fileDone);
          controller.enqueue(chunk);
        },
      });
      await sync.read(f.path).pipeThrough(counter).pipeTo(target);
      bytesDone += f.size;
      filesDone += 1;
    }
  } finally {
    await sync.dispose();
  }
  onProgress?.({ phase: "verify", filesDone, filesTotal: files.length, bytesDone, bytesTotal });
  return { bytes: bytesDone };
}

/** Post-pull byte verification: re-walk the remote and compare every size. */
async function verifyAgainstRemote(
  adb: Adb, root: string, base: string[], writer: DirWriter,
): Promise<string[]> {
  const after = await walkRemote(adb, root);
  const mismatches: string[] = [];
  for (const f of after) {
    const local = await writer.size([...base, ...f.rel]);
    if (local == null) mismatches.push(`${f.rel.join("/")} (missing locally)`);
    else if (local !== f.size) mismatches.push(`${f.rel.join("/")} (${local} local vs ${f.size} remote)`);
  }
  return mismatches;
}

export async function rmRemote(adb: Adb, path: string): Promise<boolean> {
  if (!/^[\w\/.:-]+$/.test(path)) throw new Error(`refusing to rm suspicious path: ${path}`);
  await adb.subprocess.spawnAndWait(`rm -rf ${path}`);
  const check = await adb.subprocess.spawnAndWait(`ls -d ${path} 2>/dev/null; true`);
  return !check.stdout.trim();
}

/** Pull one standalone session; mirror of pull-latest.sh's default mode. */
export async function pullSession(
  adb: Adb, pkg: string, id: string, writer: DirWriter,
  opts: { deleteRemote: boolean; onProgress?: (p: PullProgress) => void },
): Promise<PullResult> {
  const t0 = Date.now();
  const remote = `${recRoot(pkg)}/${id}`;
  opts.onProgress?.({ phase: "walk", filesDone: 0, filesTotal: 0, bytesDone: 0, bytesTotal: 0 });
  const files = await walkRemote(adb, remote);
  const { bytes } = await pullFiles(adb, files.map((f) => ({ ...f, rel: [id, ...f.rel] })), writer, opts.onProgress);
  const mismatches = await verifyAgainstRemote(adb, remote, [id], writer);
  let deletedRemote = false;
  if (!mismatches.length && opts.deleteRemote) {
    opts.onProgress?.({ phase: "delete", filesDone: files.length, filesTotal: files.length, bytesDone: bytes, bytesTotal: bytes });
    deletedRemote = await rmRemote(adb, remote);
  }
  opts.onProgress?.({ phase: "done", filesDone: files.length, filesTotal: files.length, bytesDone: bytes, bytesTotal: bytes });
  return { id, files: files.length, bytes, seconds: (Date.now() - t0) / 1e3, verified: !mismatches.length, mismatches, deletedRemote };
}

/**
 * Pull one task-capture collection self-contained (pull-latest.sh -c): the
 * collection dir plus every task clip into <id>/<recordingDir>/ so the
 * recordingDir paths inside collection.json resolve directly.
 */
export async function pullCollection(
  adb: Adb, pkg: string, id: string, writer: DirWriter,
  opts: { deleteRemote: boolean; onProgress?: (p: PullProgress) => void },
): Promise<PullResult> {
  const t0 = Date.now();
  const remote = `${colRoot(pkg)}/${id}`;
  const colFiles = await walkRemote(adb, remote);
  const { bytes: colBytes } = await pullFiles(adb, colFiles.map((f) => ({ ...f, rel: [id, ...f.rel] })), writer, opts.onProgress);

  // Parse the freshly-pulled collection.json for the clips it references.
  let recDirs: string[] = [];
  try {
    const json = JSON.parse(await writer.readText([id, "collection.json"]));
    recDirs = (json.tasks || [])
      .map((t: any) => t?.recordingDir)
      .filter((rd: any) => typeof rd === "string" && rd);
  } catch {
    throw new Error(`collection ${id}: no parseable collection.json after pull`);
  }

  let bytes = colBytes, files = colFiles.length;
  const clips: { recId: string; remote: string; base: string[] }[] = [];
  for (const rd of recDirs) {
    const recId = rd.split("/").pop() ?? "";
    if (!SESSION_ID_RE.test(recId)) continue;   // odd recordingDir — skip, like the script
    const clipRemote = `${recRoot(pkg)}/${recId}`;
    const clipFiles = await walkRemote(adb, clipRemote);
    if (!clipFiles.length) continue;            // clip already pulled/deleted previously
    const base = [id, ...rd.split("/").filter(Boolean)];
    const r = await pullFiles(adb, clipFiles.map((f) => ({ ...f, rel: [...base, ...f.rel] })), writer, opts.onProgress);
    bytes += r.bytes;
    files += clipFiles.length;
    clips.push({ recId, remote: clipRemote, base });
  }

  // Verify the collection AND every clip before deleting anything remote.
  const mismatches = await verifyAgainstRemote(adb, remote, [id], writer);
  for (const c of clips) mismatches.push(...await verifyAgainstRemote(adb, c.remote, c.base, writer));

  let deletedRemote = false;
  if (!mismatches.length && opts.deleteRemote) {
    deletedRemote = await rmRemote(adb, remote);
    for (const c of clips) await rmRemote(adb, c.remote);
  }
  return { id, files, bytes, seconds: (Date.now() - t0) / 1e3, verified: !mismatches.length, mismatches, deletedRemote };
}
